/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatKg } from './constants';

const headerCellSx = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  bgcolor: 'action.hover',
  borderBottom: 2,
  borderColor: 'divider',
  py: 1.25,
  px: 1.5,
  fontSize: '0.8125rem',
};

const stickyColSx = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderRight: 1,
  borderColor: 'divider',
};

const stickyCol2Sx = {
  position: 'sticky',
  left: 72,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderRight: 1,
  borderColor: 'divider',
  minWidth: 220,
};

const numericCellSx = {
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  px: 1.5,
  py: 1,
  fontSize: '0.8125rem',
};

const buildRowsWithSubtotals = (rows, regionSubtotals, materialColumns) => {
  if (!rows?.length) return [];

  const subtotalMap = (regionSubtotals || []).reduce((acc, item) => {
    acc[item.regiao] = item;
    return acc;
  }, {});

  const result = [];
  let currentRegiao = null;

  rows.forEach((row) => {
    if (currentRegiao && row.regiao !== currentRegiao) {
      const sub = subtotalMap[currentRegiao];
      if (sub) {
        result.push({ type: 'subtotal', regiao: currentRegiao, ...sub });
      }
    }
    if (row.regiao !== currentRegiao) {
      currentRegiao = row.regiao;
    }
    result.push({ type: 'data', ...row });
  });

  if (currentRegiao && subtotalMap[currentRegiao]) {
    result.push({ type: 'subtotal', regiao: currentRegiao, ...subtotalMap[currentRegiao] });
  } else if (currentRegiao) {
    const regionRows = rows.filter((r) => r.regiao === currentRegiao);
    const pesos = {};
    materialColumns.forEach((col) => {
      pesos[col.tipo] = regionRows.reduce(
        (sum, r) => sum + (Number(r.pesos?.[col.tipo]) || 0),
        0,
      );
    });
    const totalKg = regionRows.reduce((sum, r) => sum + (Number(r.totalKg) || 0), 0);
    if (regionRows.length > 1) {
      result.push({ type: 'subtotal', regiao: currentRegiao, pesos, totalKg });
    }
  }

  return result;
};

const PesagemMatrix = ({ matrix, loading, selectedRowKey, onRowSelect }) => {
  const { materialColumns, rows, totalsByMaterial, totalKg } = matrix;

  const displayRows = useMemo(
    () => buildRowsWithSubtotals(rows, matrix.regionSubtotals, materialColumns),
    [rows, matrix.regionSubtotals, materialColumns],
  );

  const rowKey = (row) => `${row.nsirId || ''}-${row.municipio || ''}`;

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
        <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 1 }} />
      </Paper>
    );
  }

  if (!rows?.length) {
    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">
          Nenhuma pesagem encontrada para o período selecionado.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, ...stickyColSx, minWidth: 72 }}>Região</TableCell>
              <TableCell sx={{ ...headerCellSx, ...stickyCol2Sx }}>Município</TableCell>
              {materialColumns.map((col) => (
                <TableCell key={col.tipo} sx={{ ...headerCellSx, ...numericCellSx }}>
                  {col.label}
                </TableCell>
              ))}
              <TableCell sx={{ ...headerCellSx, ...numericCellSx, fontWeight: 800 }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((row, index) => {
              if (row.type === 'subtotal') {
                return (
                  <TableRow
                    key={`subtotal-${row.regiao}-${index}`}
                    sx={{ bgcolor: 'action.selected' }}
                  >
                    <TableCell
                      colSpan={2}
                      sx={{ ...stickyColSx, fontWeight: 700, fontSize: '0.8125rem', py: 1 }}
                    >
                      Subtotal {row.regiao}
                    </TableCell>
                    {materialColumns.map((col) => (
                      <TableCell key={col.tipo} sx={{ ...numericCellSx, fontWeight: 700 }}>
                        {formatKg(row.pesos?.[col.tipo])}
                      </TableCell>
                    ))}
                    <TableCell sx={{ ...numericCellSx, fontWeight: 800 }}>
                      {formatKg(row.totalKg)}
                    </TableCell>
                  </TableRow>
                );
              }

              const key = rowKey(row);
              const isSelected = selectedRowKey === key;

              return (
                <TableRow
                  key={key}
                  hover
                  selected={isSelected}
                  onClick={() => onRowSelect?.(isSelected ? null : row)}
                  sx={{
                    cursor: onRowSelect ? 'pointer' : 'default',
                    '&:nth-of-type(even)': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell sx={{ ...stickyColSx, fontWeight: 600, fontSize: '0.8125rem' }}>
                    {row.regiao || '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...stickyCol2Sx,
                      fontSize: '0.8125rem',
                      maxWidth: 280,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                    title={row.municipio}
                  >
                    {row.municipio || '—'}
                  </TableCell>
                  {materialColumns.map((col) => (
                    <TableCell key={col.tipo} sx={numericCellSx}>
                      {formatKg(row.pesos?.[col.tipo])}
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...numericCellSx, fontWeight: 700 }}>
                    {formatKg(row.totalKg)}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ bgcolor: 'action.selected' }}>
              <TableCell colSpan={2} sx={{ ...stickyColSx, fontWeight: 800, py: 1.25 }}>
                Total geral
              </TableCell>
              {materialColumns.map((col) => (
                <TableCell key={col.tipo} sx={{ ...numericCellSx, fontWeight: 800 }}>
                  {formatKg(totalsByMaterial?.[col.tipo])}
                </TableCell>
              ))}
              <TableCell sx={{ ...numericCellSx, fontWeight: 800 }}>
                {formatKg(totalKg)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

PesagemMatrix.propTypes = {
  matrix: PropTypes.shape({
    materialColumns: PropTypes.array,
    rows: PropTypes.array,
    totalsByMaterial: PropTypes.object,
    totalKg: PropTypes.number,
    regionSubtotals: PropTypes.array,
  }).isRequired,
  loading: PropTypes.bool,
  selectedRowKey: PropTypes.string,
  onRowSelect: PropTypes.func,
};

PesagemMatrix.defaultProps = {
  loading: false,
  selectedRowKey: null,
  onRowSelect: null,
};

export default PesagemMatrix;
